import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Subject} from 'rxjs';
import { PublicApiService } from '../../services/public-api.service';
import {Location} from "../../models/location.model";
import * as Highcharts from 'highcharts';
import * as moment from "moment-timezone";

interface QuerySegmentData {
  is_register:boolean,
  label:string,
  raw_ts_data:any[],
  chart_data:Array<[number, number|null]>
}

@Component({
  selector: 'app-location-plots',
  templateUrl: './location-plots.component.html',
  styleUrls: ['./location-plots.component.scss']
})
export class LocationPlotsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() locationId: string|undefined = '';
  @Input() selectedLocation: Location|null = null;


  loading: boolean = false;
  error: string = '';
  hasGapsStart: string = ''
  hasGapsEnd: string = ''
  hasGapsWithinData: string = '';

  // Controls visibility of the plot area (card body)
  showPlotBody: boolean = false;
  // Global toggles (persist across location changes)
  includeRegisters: boolean = false;
  displayRegisters: boolean = false;
  selectedLocationDetails: any = {};
  raw_time_series_data: any ={};

  // Calendar day selector (defaults to today, bound in template)
  selectedDate: string = '';

  // Interval duration selector: '1h' (Hourly) or '15m' (15 minute). Defaults to Hourly.
  intervalDurationSelected: '15m' | '1h' = '15m';

  // Highcharts bindings for the template
  Highcharts: typeof Highcharts = Highcharts;

  chartOptions: Highcharts.Options = {
    title: { text: '' },
    yAxis: { title: { text: 'Wh' } },
    xAxis: { type: 'datetime', labels: { format: '{value:%Y-%m-%d %H:%M}'}}, // ISO-like format for labels
    legend: { enabled: true },
    tooltip: { shared: true },
    series: [],
    chart: { zooming: { type: 'x' as Highcharts.OptionsChartZoomingTypeValue }},
  };

  updateFlag = false;

  // Cached series to allow toggling display of registers without refetching
  private currentMainSeries: Highcharts.SeriesLineOptions[] = [];
  private currentRegisterSeries: Highcharts.SeriesLineOptions[] = [];

  private destroy$ = new Subject<void>();


  constructor(private publicApiService: PublicApiService
  ) { }

  ngOnInit(): void {
    // Initialize date selector to today (local time) in YYYY-MM-DD format compatible with <input type="date">
    this.selectedDate = this.formatDateForInput(new Date());
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Clear plot and related state when a different location is selected
    if (changes['locationId']) {
      this.loading = false;
      this.error = '';
      this.hasGapsStart = '';
      this.hasGapsEnd = '';
      this.showPlotBody = false; // hide plot section until user clicks again
      this.selectedLocationDetails = {};
      this.raw_time_series_data = {};
      // Do NOT reset includeRegisters/displayRegisters here; they are global per requirements

      // Clear Highcharts series and trigger an update
      this.chartOptions = {
        ...this.chartOptions,
        series: []
      };
      this.updateFlag = !this.updateFlag;
      // Also clear cached series
      this.currentMainSeries = [];
      this.currentRegisterSeries = [];
    }
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected readonly Math = Math;


  async onShowPlotsButtonClick(locationId: any) {

    // Reveal plot section on user request
    this.showPlotBody = true;
    this.loading = true;

    if(this.locationId!== undefined){

      this.publicApiService.getLocationQuerySegments(locationId).subscribe({
        next: qsData => {

          const querySegments: Array<any> = qsData;

          const querySegmentData: Map<string,QuerySegmentData> = new Map<string,QuerySegmentData>();

          let hasEnphaseOrCurb = false;

          for(const seg of querySegments){
            if(this.includeRegisters && seg.type === "register"){
              querySegmentData.set(seg.id,{is_register:true, label: seg.label,raw_ts_data:[],chart_data:[]});
            }
            if(seg.label==='Enphase Production' || seg.label==='Enphase Consumption'){
              querySegmentData.set(seg.id,{is_register:false, label: seg.label,raw_ts_data:[],chart_data:[]});
              hasEnphaseOrCurb = true;
            }
            if(seg.label==='Total consumption'){
              querySegmentData.set(seg.id,{is_register:false, label: "Curb Consumption",raw_ts_data:[],chart_data:[]});
              hasEnphaseOrCurb = true;
            }
            if(seg.label==='Total production'){
              querySegmentData.set(seg.id,{is_register:false, label: "Curb Production",raw_ts_data:[],chart_data:[]});
              hasEnphaseOrCurb = true;
            }
          }

          if(!hasEnphaseOrCurb){
            this.error = "No Enphase or Curb registers configured";
            this.loading = false;
            return;
          }

          const querySegmentsRequested = querySegments.reduce((accumulator:any, current:any)=> {
            if(
              (this.includeRegisters && current.type === "register") ||
              current.type === "external" ||
              (current.type === "total" && (current.label==="Total consumption") || (current.label==="Total production"))
            ) {
              accumulator[current.id] = ['e'];
              return accumulator;
            }
            return accumulator;
          }, {});


          // Use user-selected interval size for fetching and processing
          let requestedIntervalSize: '15m' | '1h' = this.intervalDurationSelected;

          const currentDate: Date = new Date(this.selectedDate);
          currentDate.setHours(0, 0, 0, 0);

          let momentTsEnd:moment.Moment = moment.tz(currentDate,this.selectedLocationDetails.timezone);
          let momentTsStart:moment.Moment = momentTsEnd.clone().subtract(14, 'days');

          const requestedEndDate:moment.Moment = momentTsEnd.clone();
          let requestedAPIEndDate:moment.Moment;

          requestedAPIEndDate = requestedEndDate.clone().add(15, 'minutes');

          if(requestedIntervalSize === '15m'){
            requestedAPIEndDate = requestedEndDate.clone().add(15, 'minutes');
          }
          if(requestedIntervalSize === '1h'){
            requestedAPIEndDate = requestedEndDate.clone().add(1, 'hours');
          }

          const range_end_str = requestedAPIEndDate.toISOString();

          const requestedStartDate:moment.Moment = momentTsStart.clone();
          const range_start_str = requestedStartDate.toISOString();

          console.log("range_start_str",range_start_str);
          console.log("range_end_str",range_end_str);

          this.publicApiService.getLocationTimeSeries(locationId,requestedIntervalSize,querySegmentsRequested,range_start_str,range_end_str).subscribe({
            next: tsData => {

              if(tsData.length === 0 ){
                this.error = "No time series data found for the location";
                this.loading = false;
                return;
              }

              let len = 0;

              // Populate raw register data only if registers were requested
              querySegmentData.forEach((value: QuerySegmentData, key: string) => {
                value.raw_ts_data = tsData[0].series[key].e;
                len = value.raw_ts_data.length;
              });

              const returnedStartDate:moment.Moment = moment.tz(tsData[0].start,this.selectedLocationDetails.timezone);
              let returnedEndDate:moment.Moment;

              returnedEndDate = returnedStartDate.clone().add((15 * len), 'minutes');
              if(requestedIntervalSize === '15m'){
                returnedEndDate = returnedStartDate.clone().add((15 * len), 'minutes');
              }
              if(requestedIntervalSize === '1h'){
                returnedEndDate = returnedStartDate.clone().add(len, 'hours');
              }

              console.log("returnedStartDate",returnedStartDate.toISOString());
              console.log("returnedEndDate",returnedEndDate.toISOString());

              // Highcharts expects tuples of [millis, value] where value can be number or null

              let dateIter:moment.Moment = requestedStartDate;

              let i = 0;
              let gapCntStart = 0;
              let gapCntEnd = 0;
              let gapCntWithinData = 0;

              while(dateIter < requestedEndDate){

                if(dateIter < returnedStartDate){
                  gapCntStart++;
                  querySegmentData.forEach((value: QuerySegmentData, key: string) => {
                    value.chart_data.push([dateIter.valueOf(), null]);
                  });
                }

                if(dateIter > returnedStartDate && dateIter < returnedEndDate){
                  let gap = false;
                  querySegmentData.forEach((value: QuerySegmentData, key: string) => {
                    const v = value.raw_ts_data[i];
                    if(v === null || v === undefined){
                      gap = true;
                    }
                    value.chart_data.push([dateIter.valueOf(), (v === null || v === undefined) ? null : Number(v)]);
                  });

                  if(gap){
                    gapCntWithinData++;
                  }
                  i++;
                }

                if(dateIter > returnedEndDate){
                  gapCntEnd++;
                  querySegmentData.forEach((value: QuerySegmentData, key: string) => {
                    value.chart_data.push([dateIter.valueOf(), null]);
                  });
                }

                if(requestedIntervalSize === '15m') {
                  dateIter = dateIter.clone().add(15, 'minutes');
                }
                if(requestedIntervalSize === '1h') {
                  dateIter = dateIter.clone().add(1, 'hour');
                }
              }

              if(gapCntStart > 0){
                this.hasGapsStart = "There are " + gapCntStart + " gaps at the start";
              }
              if(gapCntEnd > 0){
                this.hasGapsEnd = "There are " + gapCntEnd + " gaps at the end";
              }
              if(gapCntWithinData > 0){
                this.hasGapsWithinData = "There are " + gapCntWithinData + " gaps within the data";
              }


              const mainSeries: Highcharts.SeriesLineOptions[] = [];

              const registerSeries: Highcharts.SeriesLineOptions[] = [];
              querySegmentData.forEach((value: QuerySegmentData) => {
                if(value.is_register) {
                  registerSeries.push({
                    type: 'line',
                    name: value.label,
                    data: value.chart_data,
                    tooltip: {valueDecimals: 2}
                  });
                } else {
                  mainSeries.push({
                    type: 'line',
                    name: value.label,
                    data: value.chart_data,
                    tooltip: {valueDecimals: 2}
                  });
                }
              });

              // Cache series so we can toggle visibility without refetch
              this.currentMainSeries = mainSeries;
              this.currentRegisterSeries = registerSeries;

              // Compose final series depending on displayRegisters flag
              const seriesArray: Highcharts.SeriesLineOptions[] = this.displayRegisters
                ? [...mainSeries, ...registerSeries]
                : [...mainSeries];

              try {
                this.chartOptions = {
                  ...this.chartOptions,
                  title: { text: `14 days ending ${this.selectedDate}` },
                  series: seriesArray as Highcharts.SeriesOptionsType[]
                };
                this.updateFlag = !this.updateFlag;
              } catch (err) {
                console.error('Error building Highcharts series from raw_time_series_data', err);
                this.error = 'Error building Highcharts series from raw_time_series_data';
                this.loading = false;
              }
              this.loading = false;
            },
            error: err => {
              console.error(`Error loading location details for UUID ${locationId}:`, err);
              this.error = "Error loading location details";
              this.loading = false;
            },
          });

        },
        error: err => {
          console.error(`Error loading location details for UUID ${locationId}:`, err);
          this.error = "Error loading location details";
          this.loading = false;
        },
      });
    }
  }

  // Toggle handler to show/hide register series without refetching
  onDisplayRegistersChange() {
    const seriesArray: Highcharts.SeriesLineOptions[] = this.displayRegisters
      ? [...this.currentMainSeries, ...this.currentRegisterSeries]
      : [...this.currentMainSeries];

    this.chartOptions = {
      ...this.chartOptions,
      series: seriesArray as Highcharts.SeriesOptionsType[]
    };
    this.updateFlag = !this.updateFlag;
  }

  // Refetch when date selector changes (only if plot is visible)
  onSelectedDateChange() {
    if (this.showPlotBody && this.locationId) {
      // Re-fetch using the new selectedDate and refresh the plot
      this.onShowPlotsButtonClick(this.locationId);
    }
  }

  // Refetch when interval selector changes (only if plot is visible)
  onIntervalChange() {
    if (this.showPlotBody && this.locationId) {
      this.onShowPlotsButtonClick(this.locationId);
    }
  }

  // Utilities
  private formatDateForInput(d: Date): string {
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
